import { useState, useEffect, useContext, createContext } from 'react';
import axios from 'axios';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();

// Create context for permissions
const PermissionsContext = createContext(null);

// Provider component
export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/users/me/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPermissions(response.data.permissions);
      setError(null);
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError(err.message);
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const value = {
    permissions,
    loading,
    error,
    refetch: fetchPermissions
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

// Custom hook to use permissions
export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  
  if (context === null) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }

  const { permissions, loading, error, refetch } = context;

  /**
   * Check if user has a specific permission
   * @param {string} category - e.g., 'photos', 'projects', 'services'
   * @param {string} action - e.g., 'view', 'create', 'edit', 'delete', 'upload'
   * @returns {boolean}
   */
  const can = (category, action) => {
    if (!permissions) return false;
    return permissions[category]?.[action] === true;
  };

  /**
   * Check if user has any of the specified permissions
   * @param {Array} permissionsList - Array of [category, action] pairs
   * @returns {boolean}
   */
  const canAny = (permissionsList) => {
    return permissionsList.some(([category, action]) => can(category, action));
  };

  /**
   * Check if user has all of the specified permissions
   * @param {Array} permissionsList - Array of [category, action] pairs
   * @returns {boolean}
   */
  const canAll = (permissionsList) => {
    return permissionsList.every(([category, action]) => can(category, action));
  };

  return {
    permissions,
    loading,
    error,
    can,
    canAny,
    canAll,
    refetch
  };
};

export default usePermissions;
